import { BlobHelpers, useBoolean } from '@wyz/utils';
import { useNavigate } from 'react-router';
import { useApplicationContext, useConfig, usePlugin } from '~/common/context';
import { useManageQuoteCart } from '~/purchase/api';
import {
  IAddCartQuoteVariable,
  IQuoteCartAction,
  IQuoteCartAddServicePayload,
  IQuoteCartSubmitPayload,
  IRimType,
} from '~/purchase/types';

export const useQuoteCart = () => {
  const pluginContext = usePlugin();

  const manageCart = useManageQuoteCart({
    submit: { enabled: true },
    updatePrices: { enabled: true },
    addService: { enabled: true },
    removeService: { enabled: true },
    updateRimType: { enabled: true },
    updateItemQuantity: { enabled: true },
    removeItem: { enabled: true },
    addItem: { enabled: true },
    reset: { enabled: true },
  });
  const navigate = useNavigate();
  const { apiUrl, tenantCode } = useConfig();
  const { auth } = useApplicationContext();

  const [remoteProcessing, remoteProcessingHandlers] = useBoolean();
  const actions: IQuoteCartAction = {
    addService: {
      process: (data: IQuoteCartAddServicePayload, onSuccess?: () => void) => {
        manageCart.addService.mutate(data, {
          onSuccess: () => {
            onSuccess?.();
          },
        });
      },
      isLoading: manageCart.addService.isLoading,
    },
    updatePrice: {
      process: (
        data: {
          items?: Array<{ id: string; price: number }>;
          services?: Array<{ id: string; price: number }>;
        },
        onSuccess?: () => void,
      ) => {
        manageCart.updatePrices.mutate(data, {
          onSuccess: () => {
            onSuccess?.();
          },
        });
      },
      isLoading: manageCart.updatePrices.isLoading,
    },
    removeItem: {
      process: async (id: string, payload?: IAddCartQuoteVariable) => {
        if (payload) {
          try {
            remoteProcessingHandlers.setTrue();
            await pluginContext.api.onDeleteFromCart?.(payload);
          } finally {
            remoteProcessingHandlers.setFalse();
          }
        }
        if (
          !pluginContext.api.config?.onDeleteFromCart?.disableDefaultBehavior
        ) {
          manageCart.removeItem.mutate(id);
        }
      },
      isLoading: manageCart.removeItem.isLoading || remoteProcessing,
    },
    updateRimType: {
      process: (data: { rimType: IRimType }) => {
        manageCart.updateRimType.mutate(data);
      },
      isLoading: manageCart.updateRimType.isLoading,
    },
    updateItemQuantity: {
      process: (id: string, body: { quantity: number }) => {
        manageCart.updateItemQuantity.mutate({ id, body });
      },
      isLoading: manageCart.updateItemQuantity.isLoading,
    },
    removeService: {
      process: (id: string) => {
        manageCart.removeService.mutate(id);
      },
      isLoading: manageCart.removeService.isLoading,
    },
    addItem: {
      process: async (data: IAddCartQuoteVariable) => {
        try {
          remoteProcessingHandlers.setTrue();
          await pluginContext.api.onAddToCart?.(data);
        } finally {
          remoteProcessingHandlers.setFalse();
        }
        if (!pluginContext.api.config?.onAddToCart?.disableDefaultBehavior) {
          manageCart.addItem.mutate(data);
        }
      },
      isLoading: manageCart.addItem.isLoading || remoteProcessing,
    },
    reset: {
      process: () => {
        manageCart.reset.mutate();
      },
      isLoading: manageCart.reset.isLoading,
    },
    submit: {
      process: ({ sendMail, print, payload, customerId }) => {
        manageCart.submit.mutate(
          { ...(payload as IQuoteCartSubmitPayload), sendMail },
          {
            onSuccess: ({ data }) => {
              const reference = data.reference;

              if (print) {
                BlobHelpers.download({
                  url: `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${customerId}/quotes/${reference}/print`,
                  token: auth.user?.access_token ?? '',
                  inline: true,
                }).catch(() => {});
              }
              void navigate(`${reference}/submitted`);
            },
          },
        );
      },
      isLoading: manageCart.submit.isLoading,
    },
  };

  return { actions };
};
